import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  ChevronDoubleLeftIcon,
  MagnifyingGlassIcon,
  SunIcon,
  MoonIcon,
  BellIcon,
  Bars3Icon,
  CheckIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useTheme } from '../../hooks/useTheme';
import { useIsMobile } from '../../hooks/useIsMobile';
import { useFocusTrap } from '../../hooks/useFocusTrap';
import { useRotuloRotaAtiva } from './navConfig';
import {
  useNotificacoes,
  useMarcarNotificacaoLida,
  useMarcarTodasLidas,
  useDispensarNotificacao,
  useDispensarTodas,
} from '../../hooks/useNotificacoes';
import type { Notificacao, SeveridadeNotif } from '../../api/notificacoes';
import { fmtTempoRelativo } from '../../utils/formatters';
import type { Density } from '../../contexts/theme';

interface TopbarProps {
  collapsed: boolean;
  onToggleCollapsed: () => void;
  onAbrirPalette: () => void;
  onAbrirMobileMenu: () => void;
}

const isMac = typeof navigator !== 'undefined' && /Mac/.test(navigator.platform);

const Topbar: React.FC<TopbarProps> = ({ collapsed, onToggleCollapsed, onAbrirPalette, onAbrirMobileMenu }) => {
  const { theme, toggleTheme, density, setDensity } = useTheme();
  const rotulo = useRotuloRotaAtiva();
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  // Notificações — feed real (GET + push SignalR via useNotificacoes).
  const [notifAberto, setNotifAberto] = useState(false);
  const notifRef = useRef<HTMLDivElement>(null);
  const { data: notif } = useNotificacoes();
  const marcarLida = useMarcarNotificacaoLida();
  const marcarTodas = useMarcarTodasLidas();
  const dispensar = useDispensarNotificacao();
  const dispensarTodas = useDispensarTodas();
  const itens = notif?.itens ?? [];
  const naoLidas = notif?.naoLidas ?? 0;

  const abrirNotificacao = (n: Notificacao) => {
    if (!n.lida) marcarLida.mutate(n.id);
    setNotifAberto(false);
    if (n.link) navigate(n.link);
  };

  // Desktop: fecha ao clicar fora do dropdown (ancorado no sino). No mobile o
  // feed vira bottom-sheet renderizado em portal FORA do notifRef — o
  // mousedown-fora fecharia na hora ao tocar dentro do sheet, então esse
  // handler é exclusivo do desktop; no mobile quem fecha é o scrim + ESC.
  useEffect(() => {
    if (!notifAberto || isMobile) return;
    const fechar = (e: MouseEvent) => {
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) setNotifAberto(false);
    };
    document.addEventListener('mousedown', fechar);
    return () => document.removeEventListener('mousedown', fechar);
  }, [notifAberto, isMobile]);

  // Mobile: trava o scroll do body enquanto o bottom-sheet está aberto.
  useEffect(() => {
    if (!notifAberto || !isMobile) return;
    const orig = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = orig;
    };
  }, [notifAberto, isMobile]);

  // Foco inicial no X + trap de Tab + ESC fecha — hook padrão de modais da casa
  // (mesmo usado em UserDialog/FotoProduto). Só ativo no bottom-sheet mobile;
  // o dropdown desktop é popover não-modal e não prende foco.
  const sheetRef = useRef<HTMLDivElement>(null);
  useFocusTrap(sheetRef, notifAberto && isMobile, () => setNotifAberto(false));

  const seg = (ativo: boolean) =>
    `rounded-[5px] px-2.5 py-1 text-xs font-medium transition-colors duration-150 ${
      ativo ? 'bg-surface text-tx shadow-sm' : 'text-tx-3 hover:text-tx-2'
    }`;

  const acoes = (
    <AcoesNotif
      naoLidas={naoLidas}
      temItens={itens.length > 0}
      onMarcarTodas={() => marcarTodas.mutate()}
      marcandoTodas={marcarTodas.isPending}
      onLimparTodas={() => dispensarTodas.mutate()}
      limpandoTodas={dispensarTodas.isPending}
    />
  );

  const lista = (
    <NotificacoesLista
      itens={itens}
      onAbrir={abrirNotificacao}
      onDispensar={(id) => dispensar.mutate(id)}
    />
  );

  return (
    <header className="app-chrome flex h-[var(--topbar-h)] flex-shrink-0 items-center gap-3 border-b border-line bg-sidebar-bg px-3 md:px-[18px]">
      {/* Mobile: hamburger abre a gaveta */}
      <button
        onClick={onAbrirMobileMenu}
        className="rounded-iris-sm p-2.5 text-tx-2 transition-colors hover:bg-surface-2 hover:text-tx md:hidden"
        aria-label="Abrir menu"
      >
        <Bars3Icon className="h-5 w-5" />
      </button>

      {/* Desktop: recolher sidebar (gira 180° quando recolhida) */}
      <button
        onClick={onToggleCollapsed}
        className="hidden rounded-iris-sm p-1.5 text-tx-2 transition-colors hover:bg-surface-2 hover:text-tx md:block"
        title={collapsed ? 'Expandir menu' : 'Recolher menu'}
        aria-label={collapsed ? 'Expandir menu' : 'Recolher menu'}
      >
        <ChevronDoubleLeftIcon
          className="h-[17px] w-[17px] transition-transform duration-200"
          style={{ transform: collapsed ? 'rotate(180deg)' : undefined }}
        />
      </button>

      {/* Breadcrumb */}
      <div className="min-w-0 flex items-baseline gap-1.5 text-sm">
        <span className="hidden text-tx-3 sm:inline">Bn UtilyHub</span>
        {rotulo && <span className="hidden text-tx-3 sm:inline">›</span>}
        <span className="truncate font-semibold text-tx">{rotulo || 'Home'}</span>
      </div>

      <div className="flex-1" />

      {/* Pílula de busca → Command Palette */}
      <button
        onClick={onAbrirPalette}
        className="flex h-8 items-center gap-2 rounded-iris-sm border border-line bg-surface-2 px-2.5 text-sm text-tx-3 transition-colors hover:border-line-strong hover:text-tx-2"
        title="Buscar ações e telas (Ctrl+K)"
      >
        <MagnifyingGlassIcon className="h-4 w-4" />
        <span className="hidden lg:inline">Buscar ações, telas…</span>
        <kbd className="hidden rounded border border-line bg-surface px-1.5 py-0.5 font-mono text-[10px] text-tx-3 md:inline">
          {isMac ? '⌘K' : 'Ctrl+K'}
        </kbd>
      </button>

      {/* Densidade */}
      <div className="hidden items-center gap-0.5 rounded-iris-sm bg-surface-2 p-0.5 md:flex">
        {(['comfortable', 'compact'] as Density[]).map((d) => (
          <button key={d} onClick={() => setDensity(d)} className={seg(density === d)}>
            {d === 'comfortable' ? 'Confortável' : 'Compacto'}
          </button>
        ))}
      </div>

      {/* Tema */}
      <button
        onClick={toggleTheme}
        className="hidden h-8 w-8 items-center justify-center rounded-iris-sm text-tx-2 transition-colors hover:bg-surface-2 hover:text-tx md:flex"
        title={theme === 'dark' ? 'Modo claro' : 'Modo escuro'}
      >
        {theme === 'dark' ? <SunIcon className="h-[18px] w-[18px]" /> : <MoonIcon className="h-[18px] w-[18px]" />}
      </button>

      {/* Notificações — feed real com badge de não-lidas e push em tempo real.
          Visível em qualquer viewport; no mobile o painel abre como bottom-sheet. */}
      <div className="relative" ref={notifRef}>
        <button
          onClick={() => setNotifAberto((v) => !v)}
          className="relative flex h-11 w-11 items-center justify-center rounded-iris-sm text-tx-2 transition-colors hover:bg-surface-2 hover:text-tx md:h-8 md:w-8"
          title="Notificações"
          aria-label={naoLidas > 0 ? `Notificações (${naoLidas} não lidas)` : 'Notificações'}
        >
          <BellIcon className="h-[20px] w-[20px] md:h-[18px] md:w-[18px]" />
          {naoLidas > 0 && (
            <span className="absolute right-1 top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-accent px-1 text-[10px] font-semibold leading-none text-white md:-right-0.5 md:-top-0.5">
              {naoLidas > 99 ? '99+' : naoLidas}
            </span>
          )}
        </button>

        {/* Desktop: dropdown ancorado no sino */}
        {notifAberto && !isMobile && (
          <div className="absolute right-0 top-full z-50 mt-1 w-80 overflow-hidden rounded-iris border border-line bg-surface shadow-iris-xl">
            <div className="flex items-center justify-between gap-2 border-b border-line px-4 py-2.5">
              <h2 className="text-sm font-semibold text-tx">Notificações</h2>
              {acoes}
            </div>
            <div className="max-h-[380px] overflow-y-auto">{lista}</div>
          </div>
        )}

        {/* Mobile: bottom-sheet (portal pro body pra escapar do fluxo do header) */}
        {notifAberto && isMobile &&
          createPortal(
            <div
              className="fixed inset-0 z-50 flex flex-col justify-end"
              onClick={() => setNotifAberto(false)}
            >
              <div className="absolute inset-0 bg-[var(--scrim)] backdrop-blur-[2px]" />
              <div
                ref={sheetRef}
                role="dialog"
                aria-modal="true"
                aria-label="Notificações"
                onClick={(e) => e.stopPropagation()}
                className="relative flex max-h-[85vh] flex-col rounded-t-iris-lg border-t border-line-strong bg-surface shadow-iris-xl"
              >
                <div className="flex items-center justify-between border-b border-line px-4 py-3">
                  <h2 className="text-sm font-semibold text-tx">Notificações</h2>
                  <button
                    type="button"
                    onClick={() => setNotifAberto(false)}
                    aria-label="Fechar"
                    className="flex h-11 w-11 items-center justify-center rounded-iris text-tx-3 hover:bg-surface-2"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>
                {(naoLidas > 0 || itens.length > 0) && (
                  <div className="border-b border-line px-4 py-2.5">{acoes}</div>
                )}
                <div className="flex-1 overflow-y-auto">{lista}</div>
              </div>
            </div>,
            document.body,
          )}
      </div>
    </header>
  );
};

/** Ações do topo do feed — "Marcar lidas" / "Limpar" (compartilhado dropdown ↔ sheet). */
const AcoesNotif: React.FC<{
  naoLidas: number;
  temItens: boolean;
  onMarcarTodas: () => void;
  marcandoTodas: boolean;
  onLimparTodas: () => void;
  limpandoTodas: boolean;
}> = ({ naoLidas, temItens, onMarcarTodas, marcandoTodas, onLimparTodas, limpandoTodas }) => (
  <div className="flex items-center gap-3">
    {naoLidas > 0 && (
      <button
        onClick={onMarcarTodas}
        disabled={marcandoTodas}
        className="flex min-h-11 items-center gap-1 text-xs font-medium text-tx-3 transition-colors hover:text-tx disabled:opacity-50 md:min-h-0"
      >
        <CheckIcon className="h-3.5 w-3.5" />
        Marcar lidas
      </button>
    )}
    {temItens && (
      <button
        onClick={onLimparTodas}
        disabled={limpandoTodas}
        className="flex min-h-11 items-center gap-1 text-xs font-medium text-tx-3 transition-colors hover:text-tx disabled:opacity-50 md:min-h-0"
      >
        <XMarkIcon className="h-3.5 w-3.5" />
        Limpar
      </button>
    )}
  </div>
);

/** Lista de notificações (empty state + itens). Compartilhado dropdown ↔ sheet. */
const NotificacoesLista: React.FC<{
  itens: Notificacao[];
  onAbrir: (n: Notificacao) => void;
  onDispensar: (id: number) => void;
}> = ({ itens, onAbrir, onDispensar }) => {
  if (itens.length === 0) {
    return <p className="px-4 py-8 text-center text-sm text-tx-3">Sem notificações por enquanto.</p>;
  }
  return (
    <ul className="divide-y divide-line">
      {itens.map((n) => (
        <li
          key={n.id}
          className={`group relative flex items-stretch transition-colors hover:bg-surface-2 ${
            n.lida ? '' : 'bg-accent-soft'
          }`}
        >
          <button
            onClick={() => onAbrir(n)}
            className="flex min-w-0 flex-1 items-start gap-2.5 py-3 pl-4 pr-14 text-left md:pr-8"
          >
            <span
              className={`mt-1.5 h-2 w-2 flex-shrink-0 rounded-full ${corSeveridade(n.severidade)} ${
                n.lida ? 'opacity-30' : ''
              }`}
            />
            <span className="min-w-0 flex-1">
              <span className={`block text-sm ${n.lida ? 'text-tx-2' : 'font-semibold text-tx'}`}>
                {n.titulo}
              </span>
              {n.corpo && <span className="mt-0.5 block truncate text-xs text-tx-3">{n.corpo}</span>}
              <span className="mt-0.5 block text-[11px] text-tx-3">{fmtTempoRelativo(n.criadoEm)}</span>
            </span>
          </button>
          {/* No mobile o dismiss é sempre visível (touch não tem hover); no desktop
              mantém o reveal por hover/foco. */}
          <button
            onClick={() => onDispensar(n.id)}
            className="absolute right-1.5 top-1.5 flex h-11 w-11 items-center justify-center rounded-iris-sm text-tx-3 opacity-100 transition-opacity hover:bg-surface hover:text-tx md:h-6 md:w-6 md:opacity-0 md:focus:opacity-100 md:group-hover:opacity-100"
            title="Limpar esta notificação"
            aria-label="Limpar notificação"
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        </li>
      ))}
    </ul>
  );
};

/** Cor do dot por severidade — alinha com a paleta semântica (rose/amber/azul). */
const corSeveridade = (s: SeveridadeNotif): string =>
  s === 'critico' ? 'bg-rose-500' : s === 'alerta' ? 'bg-amber-500' : 'bg-accent';

export default Topbar;
